import React from 'react';
import { useTranslation } from 'react-i18next';
import { Tag, Tooltip } from '@/shared/antd-imports';
import { InfoCircleOutlined } from '@ant-design/icons';
import type { AgentProgressState, PortalDisclosure } from '@/types/updates';

const COLORS: Record<AgentProgressState, string> = {
  in_progress: 'processing',
  in_review: 'purple',
  verified_by_tests: 'success',
};

const DEFAULTS: Record<AgentProgressState, string> = {
  in_progress: 'In progress',
  in_review: 'In review',
  verified_by_tests: 'Verified by tests',
};

/** Spec 28 §8.1 (stream abill): one of three states, shown only when the provider turned it on for the project. */
export const AgentProgressChip: React.FC<{ state?: AgentProgressState | null }> = ({ state }) => {
  const { t } = useTranslation();
  if (!state || !COLORS[state]) return null;
  return (
    <Tag color={COLORS[state]} bordered={false} style={{ marginInlineEnd: 0 }} data-testid="agent-progress-chip">
      {t(`agentProgress.${state}`, { defaultValue: DEFAULTS[state] })}
    </Tag>
  );
};

/** Spec 28 §8.2: the workspace's AI-assisted label (neutral tag; the explanation on hover / focus). */
export const DisclosureTag: React.FC<{ disclosure?: PortalDisclosure | null; label?: string | null }> = ({ disclosure, label }) => {
  const { t } = useTranslation();
  const text = (disclosure?.label ?? label) || t('agentProgress.aiAssisted', { defaultValue: 'AI-assisted' });
  const tag = (
    <Tag bordered={false} style={{ marginInlineEnd: 0 }} icon={disclosure?.explanation ? <InfoCircleOutlined aria-hidden /> : undefined} data-testid="disclosure-tag" tabIndex={disclosure?.explanation ? 0 : undefined}>
      {text}
    </Tag>
  );
  return disclosure?.explanation ? <Tooltip title={disclosure.explanation}>{tag}</Tooltip> : tag;
};
